// Yahoo-specific scalar list editors. These keys deliberately do not use the
// shared DV360/TTD list panels, whose storage and add/remove semantics differ.
export const YAHOO_TARGET_FIELDS = {
  yahooGeo: 'Included geography', yahooGeoExcluded: 'Excluded geography',
  yahooSegments: 'Audience segment IDs', yahooCategories: 'Category IDs',
  yahooKeywords: 'Search keywords', yahooMailDomains: 'Mail domains',
  yahooReceipts: 'Purchase receipt values', yahooDomains: 'Site domains',
};
const ids = new Set(['yahooGeo', 'yahooGeoExcluded', 'yahooSegments', 'yahooCategories']);
const specFields = {
  yahooKeywords: ['audiences', 'search_keyword', 'keywords'],
  yahooMailDomains: ['audiences', 'maildomain', 'domains'],
  yahooReceipts: ['audiences', 'purchase_receipt', 'values'],
  yahooDomains: ['siteLists', null, 'seeds'],
};
const arr = (v) => Array.isArray(v) ? v : [];
const placeholder = (v) => /FILL OUT HERE/i.test(String(v));
const validIds = (v) => arr(v).map((x) => typeof x === 'object' ? x.entityId ?? x.contextualId ?? x.id : x).filter((x) => x != null && !placeholder(x)).map(String);
export function yahooTargetValues(value) {
  if (typeof value !== 'string' && !Array.isArray(value)) throw new Error('Yahoo targeting must be a comma-separated list or an array.');
  return [...new Set((Array.isArray(value) ? value : value.split(/[,\n]/)).map((v) => String(v).trim()).filter(Boolean))];
}
export function yahooTargetError(field, value, row) {
  if (row.yahooTargetingState?.startsWith('Not prepared')) return 'Prepare Yahoo targeting before editing this older plan; reload the draft to run preparation.';
  if (field === 'yahooSegments' && row.yahooSegmentGroups?.length > 1) return 'This line has multiple audience groups; editing a flat list would change its targeting logic.';
  let values;
  try { values = yahooTargetValues(value); } catch (e) { return e.message; }
  if (values.some(placeholder)) return 'Unresolved placeholders are not valid Yahoo targeting.';
  const geoField = ['yahooGeo', 'yahooGeoExcluded'].includes(field);
  const numericId = (v) => /^[1-9]\d*$/.test(v) && Number.isSafeInteger(Number(v));
  const geoId = (v) => numericId(v) || (/^#[^,\r\n]+$/.test(v) && /[^#\s]/.test(v));
  if (ids.has(field) && values.some((v) => !(geoField ? geoId(v) : numericId(v)))) return 'Use verified Yahoo geography IDs (including # location keys); audience segments and categories require numeric IDs. Never guess IDs.';
  if (field === 'yahooGeo' && !values.length) return 'Yahoo requires at least one included geography ID.';
  if (specFields[field] && !arr(row.yahooTargetEditable).includes(field)) return 'This Yahoo line does not have exactly one editable definition for this targeting type. Prepare targeting before editing it.';
  if (specFields[field] && !values.length) return 'A Yahoo audience or site list cannot be empty. Keep at least one value.';
  if (['yahooMailDomains', 'yahooDomains'].includes(field) && values.some((v) => !/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i.test(v))) return 'Enter domain names only, without protocols, paths or email addresses.';
  if (field === 'yahooReceipts' && row.yahooReceiptAttribute === 'farap' && values.some((v) => !/^[A-Za-z]{3}$/.test(v))) return 'Airport receipt values must be three-letter IATA codes.';
  return null;
}
function refs(line, bucket) {
  const t = line.targeting || {};
  return arr(bucket === 'audiences' ? t.audiences ?? t.audienceNames : t.siteLists).map((v) => typeof v === 'object' ? v.name : v);
}
function linked(result, line, bucket, type) {
  return arr(result[bucket]).filter((s) => (!type || s.type === type)
    && (arr(s.line_keys ?? s.lineKeys).some((k) => k === line.lineKey || k === line.name) || refs(line, bucket).includes(s.name)));
}
function exclusionsFor(spec, line, bucket) {
  if (bucket !== 'audiences') return [false];
  const t = line.targeting || {};
  const states = arr(t.audiences ?? t.audienceNames).filter((r) => (typeof r === 'object' ? r.name : r) === spec.name)
    .map((r) => Boolean(typeof r === 'object' && r.excluded));
  if (arr(spec.line_keys ?? spec.lineKeys).some((k) => k === line.lineKey || k === line.name)) states.push(Boolean(spec.excluded));
  return [...new Set(states)];
}
export function formatYahooGeography(value, row) {
  return yahooTargetValues(value).map((id) => row?.yahooGeoLabels?.[id] || id).join(' · ') || '—';
}

export function yahooTargetingRow(result, line) {
  const t = line.targeting || {}, raw = t.raw || {};
  const row = {
    yahooGeo: validIds(t.geo?.included).join(', '),
    yahooGeoExcluded: validIds(t.geo?.excluded).join(', '),
    yahooSegments: validIds(arr(raw.segments?.added).filter((s) => !s.excluded)).join(', '),
    yahooCategories: validIds(arr(raw.contextuals?.added).filter((s) => !s.excluded)).join(', '),
    yahooSegmentGroups: [...new Set(arr(raw.segments?.added).filter((s) => !s.excluded).map((s) => s.group || 'Group A'))],
    yahooTargetEditable: [],
  };
  // Use names returned by Yahoo, never derive a location from its opaque key.
  row.yahooGeoLabels = {};
  for (const resolution of arr(result.customData?.postProcessing?.resolutions)) {
    if (!['geo', 'geo-exclude'].includes(resolution.kind)) continue;
    const match = /^(.*?) \((.*)\)$/.exec(String(resolution.resolved || ''));
    if (match) row.yahooGeoLabels[match[1]] = match[2].replace(/ \/ /g, ' · ');
  }
  for (const location of [...arr(t.geo?.included), ...arr(t.geo?.excluded)]) {
    if (location && typeof location === 'object' && location.id != null && location.name) {
      row.yahooGeoLabels[String(location.id)] = [location.name, location.level].filter(Boolean).join(' · ');
    }
  }
  row.geo = t.geo?.geoInput ? `${t.geo.geoInput} (pending resolution)` : formatYahooGeography(row.yahooGeo, row);
  row.geoExclude = formatYahooGeography(row.yahooGeoExcluded, row);
  const names = linked(result, line, 'audiences').map((s) => s.name);
  row.yahooAudienceSummary = names.join(' · ') || '—';
  row.audiences = [...names, ...row.yahooSegments.split(', ').filter(Boolean)].join(' · ') || '—';
  for (const [key, [bucket, type, prop]] of Object.entries(specFields)) {
    const specs = linked(result, line, bucket, type);
    row[key] = specs.flatMap((s) => arr(s[prop] ?? (bucket === 'siteLists' ? s.domains : []))).join(', ');
    if (specs.length === 1 && exclusionsFor(specs[0], line, bucket).length <= 1) row.yahooTargetEditable.push(key);
    if (key === 'yahooReceipts') {
      row.yahooReceiptAttribute = specs[0]?.attribute_key || '';
      row.yahooReceiptType = [specs[0]?.event_type, specs[0]?.attribute_key].filter(Boolean).join(' / ') || '—';
    }
  }
  const unresolved = result.customData?.unresolved || JSON.stringify(t).includes('[FILL OUT HERE]');
  row.yahooTargetingState = unresolved ? 'Not prepared — reload the draft to prepare targeting' : 'Prepared';
  return row;
}
// Content-address new specs: Yahoo can reuse an existing audience by name, so
// leaving the old name after an edit would silently reuse the old contents.
function contentName(name, spec) {
  const text = JSON.stringify(spec);
  let a = 2166136261, b = 5381;
  for (let i = 0; i < text.length; i++) { a = Math.imul(a ^ text.charCodeAt(i), 16777619); b = Math.imul(b, 33) ^ text.charCodeAt(i); }
  return `${String(name || 'Yahoo review').replace(/\s*\[[^\]]+\]$/, '').slice(0, 160)} [review-${(a >>> 0).toString(16)}${(b >>> 0).toString(16)}]`;
}
export function applyYahooTargeting(result, line, field, value) {
  const row = yahooTargetingRow(result, line);
  const error = yahooTargetError(field, value, row);
  if (error) throw new Error(error);
  const values = yahooTargetValues(value);
  const t = (line.targeting ||= {});
  if (field === 'yahooGeo' || field === 'yahooGeoExcluded') {
    const geo = (t.geo ||= {});
    geo[field === 'yahooGeo' ? 'included' : 'excluded'] = values;
    delete geo.geoInput;
  } else if (field === 'yahooSegments' || field === 'yahooCategories') {
    const raw = (t.raw ||= {}), segment = field === 'yahooSegments';
    const key = segment ? 'segments' : 'contextuals', rule = segment ? 'SEGMENT' : 'CONTEXTUALS';
    const old = raw[key] || {};
    // Keep existing exclusions and operator metadata; replace inclusions only.
    raw[key] = { ...old, added: [...arr(old.added).filter((s) => s.excluded), ...values.map((id) => ({ [segment ? 'entityId' : 'contextualId']: Number(id), excluded: false, ...(segment ? { group: row.yahooSegmentGroups[0] || 'Group A' } : {}) }))] };
    delete raw[key].seedNames; delete raw[key].seedKeywords;
    raw.types = [...arr(raw.types).filter((x) => x.name !== rule), { name: rule, isTargeted: raw[key].added.length > 0 }];
  } else {
    const [bucket, type, prop] = specFields[field];
    const original = linked(result, line, bucket, type)[0];
    if (JSON.stringify(arr(original[prop])) === JSON.stringify(values)) return;
    // Detach only this line from a shared definition. Other lines and native
    // counterparts keep the original audience, domain list, and targeting.
    const clone = JSON.parse(JSON.stringify(original));
    clone[prop] = values;
    if (bucket === 'audiences') clone.excluded = exclusionsFor(original, line, bucket)[0] || false;
    delete clone.id; delete clone.audienceId; delete clone.siteListId;
    clone.line_keys = [line.lineKey || line.name]; delete clone.lineKeys;
    const content = { ...clone }; delete content.name; delete content.line_keys;
    clone.name = contentName(original.name, content);
    const oldKeys = arr(original.line_keys ?? original.lineKeys);
    original.line_keys = oldKeys.filter((k) => k !== line.lineKey && k !== line.name); delete original.lineKeys;
    for (const refField of bucket === 'audiences' ? ['audiences', 'audienceNames'] : ['siteLists']) {
      if (Array.isArray(t[refField])) t[refField] = t[refField].filter((r) => (typeof r === 'object' ? r.name : r) !== original.name);
    }
    const stillReferenced = (result.lines || []).some((l) => l !== line && refs(l, bucket).includes(original.name))
      || (result.campaigns || [result.campaign]).filter(Boolean).some((c) => arr(c.lines).some((l) => typeof l === 'object' && l !== line && refs(l, bucket).includes(original.name)));
    if (!original.line_keys.length && !stillReferenced) result[bucket] = result[bucket].filter((s) => s !== original);
    result[bucket].push(clone);
  }
}
